import type { Scene } from '@babylonjs/core/scene';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { LevelConfig } from '../config/level.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { forwardOf, transformPoint } from './connectors/Pose';
import type { SegmentInstance } from './segments/SegmentInstance';
import { DecorationSpacingRegistry } from './spawning/DecorationSpacingRegistry';
import { DEFAULT_DECORATIONS, decorationAdSlot, type DecorationAdSlot } from './spawning/DecorationCatalog';
import type { AdvertisingSlotDefinition } from './segments/SegmentDefinition';

/** Decorations that carry ad surfaces, by asset. */
const DECORATION_ADS: ReadonlyMap<string, readonly DecorationAdSlot[]> = new Map(
  DEFAULT_DECORATIONS.filter((d) => d.advertisingSlots?.length).map((d) => [d.assetId, d.advertisingSlots!]),
);

interface SegmentNodes {
  readonly root: TransformNode;
  readonly visuals: EntityVisual[];
}

interface LeadIn {
  readonly segmentUid: number;
  readonly roots: TransformNode[];
  readonly visuals: EntityVisual[];
}

/**
 * Visual side of the track: one root node per segment (placed at the segment
 * pose) carrying the module visual and its decorations. Other presenters
 * (obstacles, collectibles, ads, debug) parent their nodes to `rootOf()`.
 * Behind the first segment it lays a few visual-only copies of the start module,
 * so the start view does not end at the start line.
 */
export class TrackPresenter {
  private readonly nodes = new Map<number, SegmentNodes>();
  private readonly pendingDisposal: TransformNode[] = [];
  private leadIn: LeadIn | null = null;
  private readonly spacing = new DecorationSpacingRegistry();
  private readonly scratch = { x: 0, y: 0, z: 0 };
  private readonly placed = { x: 0, y: 0, z: 0, yaw: 0 };

  constructor(
    private readonly scene: Scene,
    private readonly pool: VisualPool,
    events: EventBus<GameEvents>,
    private readonly level: LevelConfig,
  ) {
    events.on('segment:spawned', ({ segment }) => this.attach(segment));
    events.on('segment:recycled', ({ segment }) => this.detach(segment));
  }

  rootOf(segment: SegmentInstance): TransformNode {
    return this.ensureNodes(segment).root;
  }

  private readonly decorationSlots = new Map<number, AdvertisingSlotDefinition[]>();

  /** Ad surfaces of the decorations standing in this segment (path coordinates) – the AdSlotBinder adds them to its slots. */
  decorationAdSlots(segment: SegmentInstance): readonly AdvertisingSlotDefinition[] {
    return this.decorationSlots.get(segment.uid) ?? [];
  }

  /** Disposes roots of recycled segments – deferred so other presenters can detach first. */
  flush(): void {
    for (const root of this.pendingDisposal) root.dispose();
    this.pendingDisposal.length = 0;
  }

  private ensureNodes(segment: SegmentInstance): SegmentNodes {
    let nodes = this.nodes.get(segment.uid);
    if (!nodes) {
      const root = new TransformNode(`SEGMENT_${segment.sequenceIndex}_${segment.definition.id}`, this.scene);
      root.position.set(segment.pose.x, segment.pose.y, segment.pose.z);
      root.rotation.y = segment.pose.yaw;
      nodes = { root, visuals: [] };
      this.nodes.set(segment.uid, nodes);
    }
    return nodes;
  }

  private attach(segment: SegmentInstance): void {
    const nodes = this.ensureNodes(segment);
    nodes.visuals.push(this.pool.acquire(segment.definition.visualAssetId, nodes.root));
    // e.g. the base road modules under a greybox set piece
    for (const part of segment.definition.extraVisuals ?? []) {
      const visual = this.pool.acquire(part.assetId, nodes.root);
      visual.root.position.set(part.position.x, part.position.y, part.position.z);
      visual.root.rotation.y = part.yaw ?? 0;
      nodes.visuals.push(visual);
    }

    for (const placement of segment.spawnPlan.decorations) {
      const socket = segment.definition.decorationSockets.find((s) => s.id === placement.socketId);
      if (!socket) continue;
      // Socket data is in path coordinates – on curves the socket follows the arc.
      const local = segment.placement(socket.position, this.placed);
      if (placement.spacing) {
        const world = transformPoint(segment.pose, local, this.scratch);
        if (!this.spacing.tryClaim(segment.uid, placement.assetId, world.x, world.z, placement.spacing)) continue;
      }
      const visual = this.pool.acquire(placement.assetId, nodes.root);
      visual.root.position.set(local.x, local.y, local.z);
      visual.root.rotation.y = local.yaw + socket.yaw + (placement.yawOffset ?? 0);
      nodes.visuals.push(visual);
      // ad surfaces of a decoration that actually stands (the roadside scaffold's banner) – bound by the AdSlotBinder
      for (const slot of DECORATION_ADS.get(placement.assetId) ?? []) {
        const list = this.decorationSlots.get(segment.uid) ?? [];
        list.push(decorationAdSlot(socket, placement.yawOffset ?? 0, slot));
        this.decorationSlots.set(segment.uid, list);
      }
    }
    if (segment.sequenceIndex === 0) this.attachLeadIn(segment);
  }

  private attachLeadIn(first: SegmentInstance): void {
    this.releaseLeadIn();
    const forward = forwardOf(first.pose.yaw);
    const roots: TransformNode[] = [];
    const visuals: EntityVisual[] = [];
    for (let k = 1; k <= this.level.leadInModules; k++) {
      const root = new TransformNode(`LEAD_IN_${k}`, this.scene);
      const offset = k * first.definition.length;
      root.position.set(first.pose.x - forward.x * offset, first.pose.y, first.pose.z - forward.z * offset);
      root.rotation.y = first.pose.yaw;
      roots.push(root);
      visuals.push(this.pool.acquire(first.definition.visualAssetId, root));
    }
    this.leadIn = { segmentUid: first.uid, roots, visuals };
  }

  private releaseLeadIn(): void {
    if (!this.leadIn) return;
    for (const visual of this.leadIn.visuals) this.pool.release(visual);
    this.pendingDisposal.push(...this.leadIn.roots);
    this.leadIn = null;
  }

  private detach(segment: SegmentInstance): void {
    if (this.leadIn?.segmentUid === segment.uid) this.releaseLeadIn();
    this.spacing.release(segment.uid);
    this.decorationSlots.delete(segment.uid);
    const nodes = this.nodes.get(segment.uid);
    if (!nodes) return;
    for (const visual of nodes.visuals) this.pool.release(visual);
    this.nodes.delete(segment.uid);
    this.pendingDisposal.push(nodes.root);
  }
}
